import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { assetCatalog, organisations, type HeldProduct, type OrgUserRow } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { ErrorNote } from "@/components/ErrorNote";
import { Field } from "@/components/Field";
import { ImageField } from "@/components/ImageCapture";
import { KV } from "@/components/KV";
import { MultiSelect } from "@/components/MultiSelect";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { StatGrid } from "@/components/StatGrid";
import {
  ROLE_LABEL, orgQk, useOrgUsers, useOrganisation, type OrgUserRow as URow,
} from "@/modules/organisations/data";
import { PersonDrawer } from "@/modules/organisations/PersonDrawer";

/** One organisation: who it IS, what it CAN DO, what it HOLDS, and who works in it.
 *
 *  THE FIVE SECTIONS ARE DATTATRAY'S, in his order: *"sub sections should be
 *  Identity (ID, Plan, Status, Abbreviation, Country), Logo update,
 *  Capabilities of that company, Allocated Products, Users."*
 *
 *  ALLOCATED SPACES ARE GONE. *"Why do we see allocated spaces to a company?
 *  We should see allocated product and model, that is it!"* A space is a
 *  partition of a product that a BENCH enters, and holding the product
 *  carries every space with it (D-102) — so the list was six rows per product
 *  that nobody chose and nobody edits from here. What a company HAS is the
 *  product, and which models within it they may build with.
 *
 *  CAPABILITIES ARE TWO INDEPENDENT FACTS, not a type. A company may
 *  manufacture, may deploy assets, or both — *"a company can be an asset
 *  deployment company as well as a product manufacturing company"* — and a
 *  single type field could not say "both".
 *
 *  IDENTITY BELONGS TO THE PARTY. `abbr` and `country` lived on the maker
 *  facet until 0074, so a fleet operator that builds nothing had neither.
 *
 *  404 MEANS BOTH "no such organisation" AND "not yours", deliberately. A
 *  distinct answer would tell a customer that CMP-014 is real.
 */
export function OrganisationDetailPage() {
  const { orgId = "" } = useParams();
  const { can } = useSession();
  const mayEdit = can(CAP.MANAGE_ORGANISATIONS);
  // THE LOGO AND THE MAKER ROW ARE THE CATALOGUE'S, so they take the
  // CATALOGUE's capability. Gating them on MANAGE_ORGANISATIONS offered a
  // button that returns 403 — caught by
  // `tests/ui/test_controls_match_capabilities`, which exists for exactly
  // this: opening a module is not permission to write what another module
  // owns.
  const mayEditMaker = can(CAP.MANAGE_CATALOG);
  const qc = useQueryClient();
  const q = useOrganisation(orgId);
  const people = useOrgUsers(orgId);
  const [person, setPerson] = useState<URow | null>(null);

  const caps = useQuery({
    queryKey: ["organisations", orgId, "capabilities"],
    queryFn: () => organisations.capabilities(orgId),
    enabled: !!orgId, staleTime: 60_000, refetchOnWindowFocus: false,
  });

  const [ident, setIdent] = useState({ abbr: "", country: "", plan: "" });
  useEffect(() => {
    if (q.data) {
      setIdent({ abbr: q.data.abbr ?? "", country: q.data.country ?? "",
        plan: q.data.plan ?? "" });
    }
  }, [q.data]);

  const [makes, setMakes] = useState<string[]>([]);
  const [deploys, setDeploys] = useState<string[]>([]);
  useEffect(() => {
    if (caps.data) { setMakes(caps.data.manufactures); setDeploys(caps.data.deploys); }
  }, [caps.data]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: orgQk.detail(orgId) });
    void qc.invalidateQueries({ queryKey: ["organisations", orgId, "capabilities"] });
    void qc.invalidateQueries({ queryKey: orgQk.list });
  };
  const saveIdent = useMutation({
    mutationFn: () => organisations.setIdentity(orgId, {
      abbr: ident.abbr.trim() || null,
      country: ident.country.trim() || null,
      plan: ident.plan.trim() || undefined }),
    onSuccess: refresh,
  });
  const saveCaps = useMutation({
    mutationFn: () => organisations.setCapabilities(orgId, {
      manufactures: makes, deploys }),
    onSuccess: refresh,
  });

  if (q.isLoading) {
    return <div className="mpage on"><div className="panel panel-pad cap">Loading…</div></div>;
  }
  if (q.isError) {
    return (
      <div className="mpage on">
        <div className="panel panel-pad">
          No such organisation, or it is not one you may see.{" "}
          <Link to="/organisations/organisations">Back to organisations</Link>
        </div>
      </div>
    );
  }

  const o = q.data!;
  const users = people.data?.users ?? [];
  const c = caps.data;
  const identDirty = ident.abbr !== (o.abbr ?? "")
    || ident.country !== (o.country ?? "") || ident.plan !== (o.plan ?? "");
  const capsDirty = !!c && (
    makes.join() !== c.manufactures.join() || deploys.join() !== c.deploys.join());

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations"
        title={o.name}
        desc="A party in the registry: it can own, hold, ship and receive. What it MAKES and what it DEPLOYS are two separate capabilities, and it may have both."
      />

      <StatGrid stats={[
        { k: "Products held", v: o.products.length },
        { k: "Manufactures", v: c?.manufactures.length ?? 0 },
        { k: "Deploys", v: o.deploys.length },
        { k: "People", v: users.length },
      ]} />

      {/* ------------------------------------------------------- identity -- */}
      <div className="section-label flush">Identity</div>
      <div className="panel panel-pad">
        <KV k="Organisation ID" v={o.organisation_id} mono />
        <KV k="Status" v={o.status} mono={false} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
          gap: 12, marginTop: 12 }}>
          <Field label="Plan">
            <Select className="fill" value={ident.plan} disabled={!mayEdit}
              onChange={(e) => setIdent({ ...ident, plan: e.target.value })}>
              {["free", "starter", "pro", "enterprise"].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </Select>
          </Field>
          <Field label="Abbreviation">
            {/* IT PREFIXES TECHNICAL MODEL NAMES, so it is unique across
                companies - two sharing one would produce two different boards
                with the same name. The server refuses the clash by name. */}
            <SearchInput className="fill" mono maxLength={6} value={ident.abbr}
              disabled={!mayEdit} placeholder="AE"
              style={{ textTransform: "uppercase" }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setIdent({ ...ident, abbr: e.target.value })} />
          </Field>
          <Field label="Country">
            <SearchInput className="fill" mono maxLength={2} value={ident.country}
              disabled={!mayEdit} placeholder="IN"
              style={{ textTransform: "uppercase" }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setIdent({ ...ident, country: e.target.value })} />
          </Field>
        </div>
        <ErrorNote panel error={saveIdent.error} />
        {mayEdit && (
          <ActionRow>
            <button className="btn red" disabled={!identDirty || saveIdent.isPending}
              onClick={() => setIdent({ abbr: o.abbr ?? "", country: o.country ?? "",
                plan: o.plan ?? "" })}>Discard</button>
            <button className="btn teal" disabled={!identDirty || saveIdent.isPending}
              onClick={() => saveIdent.mutate()}>
              {saveIdent.isPending ? "Saving…" : "Save changes"}
            </button>
          </ActionRow>
        )}
      </div>

      {/* ----------------------------------------------------------- logo -- */}
      <div className="section-label">Logo</div>
      <div className="panel panel-pad">
        {c?.manufacturer_id ? (
          <OrgLogo manufacturerId={c.manufacturer_id} mayEdit={mayEditMaker}
            onChanged={refresh} />
        ) : (
          <span className="cap">
            A logo is stored against the maker record. Tick a product under
            Capabilities and it becomes available here.
          </span>
        )}
      </div>

      {/* --------------------------------------------------- capabilities -- */}
      <div className="section-label">Capabilities</div>
      <div className="panel panel-pad">
        <div className="cap" style={{ marginBottom: 10, color: "var(--t-dim)" }}>
          What this company does. Either, both, or neither - a maker that also
          runs its own fleet is both.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
          gap: 14 }}>
          <Field label="Manufactures - which products they build">
            <MultiSelect
              options={(c?.products ?? []).map((k) => ({
                value: k.kind, label: `${k.group} · ${k.label}` }))}
              value={makes} onChange={setMakes} disabled={!mayEdit}
              emptyLabel="Builds nothing" clearLabel="Clear - builds nothing" />
          </Field>
          <Field label="Deploys - which assets they put in service">
            {/* ASSETS ONLY. A company does not put a BMS into service; it puts
                a vehicle or a battery into service with a BMS inside it. The
                server refuses a component rather than dropping it silently. */}
            <MultiSelect
              options={(c?.assets ?? []).map((k) => ({ value: k.kind, label: k.label }))}
              value={deploys} onChange={setDeploys} disabled={!mayEdit}
              emptyLabel="Deploys nothing" clearLabel="Clear - deploys nothing" />
          </Field>
        </div>
        <ErrorNote panel error={saveCaps.error} />
        {mayEdit && (
          <ActionRow>
            <span className="cap muted">
              Ticking a product makes this company selectable as its maker.
            </span>
            <button className="btn red" disabled={!capsDirty || saveCaps.isPending}
              onClick={() => { setMakes(c?.manufactures ?? []); setDeploys(c?.deploys ?? []); }}>
              Discard
            </button>
            <button className="btn teal" disabled={!capsDirty || saveCaps.isPending}
              onClick={() => saveCaps.mutate()}>
              {saveCaps.isPending ? "Saving…" : "Save changes"}
            </button>
          </ActionRow>
        )}
      </div>

      {/* ---------------------------------------------- allocated products -- */}
      <div className="section-label">Allocated products</div>
      <DataTable rows={o.products} cols={PRODUCT_COLS} rowKey={(p) => p.kind}
        empty={<div className="panel panel-pad cap">
          This organisation holds no products yet. Grant one on Products →
          Product visibility.
        </div>} />

      {/* -------------------------------------------------------- people --- */}
      <div className="section-label">People</div>
      <DataTable rows={users} cols={USER_COLS} rowKey={(u) => u.user_id}
        onRowClick={mayEdit ? setPerson : undefined}
        empty={<div className="panel panel-pad cap">
          {people.isLoading ? "Loading…" : "Nobody in this organisation yet."}
        </div>} />

      {person && <PersonDrawer user={person} onClose={() => setPerson(null)} />}
    </div>
  );
}

const PRODUCT_COLS: DataCol<HeldProduct>[] = [
  { key: "kind", label: "Product", width: 180, render: (p) => p.label },
  { key: "slug", label: "", width: 120, mono: true, render: (p) => p.kind },
  {
    // EMPTY MEANS EVERY MODEL, which is the opposite of the product list's
    // empty (D-81), so it is SAID rather than counted. "0 models" would read
    // as none.
    key: "models", label: "Models they may build with", width: 320,
    render: (p) => p.all_models
      ? <Chip tone="ok">every model</Chip>
      : <span style={{ overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", display: "block" }}
          title={p.models.map((m) => `${m.model} v${m.version}`).join(", ")}>
          {p.models.map((m) => `${m.model} v${m.version}`).join(", ") || "—"}
        </span>,
  },
];

const USER_COLS: DataCol<OrgUserRow>[] = [
  { key: "name", label: "Name", width: 170, render: (u) => u.name },
  { key: "login", label: "Login", width: 230, mono: true, render: (u) => u.login_id },
  { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] ?? u.role },
  {
    key: "mfa", label: "MFA", width: 80,
    render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip>,
  },
  {
    key: "state", label: "State", width: 130,
    render: (u) => u.status !== "active" ? <Chip tone="mute">{u.status}</Chip>
      : !u.email_verified ? <Chip tone="warn">pending setup</Chip>
        : <Chip tone="ok">active</Chip>,
  },
];

/** The logo and the maker row's own state.
 *
 *  THE LOGO IS STORED AGAINST THE MAKER RECORD because that is where the
 *  catalogue's image store keys it — copying the bytes into a second store
 *  would be the same one-thing-in-two-places D-93 removed.
 *
 *  ARCHIVE IS NOT "manufactures nothing". Emptying the Capabilities list says
 *  the company makes none of our products; archiving says we no longer deal
 *  with them as a maker at all, and the catalogue refuses it while any model
 *  still points at the row. The retired Manufacturers page could do this and
 *  the facet has to keep it — `test_the_facet_carries_everything_the_retired_page_did`.
 */
function OrgLogo({ manufacturerId, mayEdit, onChanged }: {
  manufacturerId: string; mayEdit: boolean; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: () => assetCatalog.archiveManufacturer(manufacturerId),
    onSuccess: () => { onChanged(); },
  });
  return (
    <>
    <ImageField
      src={`/modules/asset-catalog/manufacturers/${manufacturerId}/logo`}
      fit="contain" size={120} empty="no logo yet"
      meta={<span className="cap">
        {mayEdit ? "Shown beside this company's models." : "Read-only."}
      </span>}
      onUpload={async (blob) => {
        await assetCatalog.uploadLogo(manufacturerId, blob);
        void qc.invalidateQueries({ queryKey: ["asset-catalog", "manufacturers"] });
      }}
      onRemove={async () => {
        await assetCatalog.deleteLogo(manufacturerId);
        void qc.invalidateQueries({ queryKey: ["asset-catalog", "manufacturers"] });
      }}
    />
    <ErrorNote error={archive.error} />
    {mayEdit && (
      <ActionRow top={10}>
        <span className="cap muted">
          Archiving retires the maker record. Refused while any model still
          points at it - retire those first.
        </span>
        <button className="btn red" disabled={archive.isPending}
          onClick={() => archive.mutate()}>Archive maker</button>
      </ActionRow>
    )}
    </>
  );
}
